import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Plus, Users, Sparkles, MessageSquare, X, LogIn, MoreHorizontal, Trash2, EyeOff } from "lucide-react";
import { apiFetch } from "@/api";
import { Button, Field, Spinner } from "@/ui";
import { BottomNav } from "@/components/BottomNav";
import { ConnectedCircles } from "@/components/ConnectedCircles";

function preview(m) {
  if (!m) return "No messages yet";
  const who = m.kind === "maxx" ? "MAXx" : m.sender_name || "Someone";
  return `${who}: ${m.text || ""}`;
}

export default function Social() {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // "new" | "join"
  const [name, setName] = useState("");
  const [joinValue, setJoinValue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRooms((await apiFetch("/social/rooms")).rooms || []);
    } catch (e) {
      if (e.status === 402) return navigate("/paywall");
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const createRoom = async () => {
    setBusy(true);
    try {
      const d = await apiFetch("/social/rooms", { method: "POST", body: { name: name.trim() } });
      setModal(null); setName("");
      navigate(`/social/${d.room.id}?invite=1`);
    } catch (e) {
      if (e.status === 402) return navigate("/paywall");
      toast(e.message);
    } finally { setBusy(false); }
  };

  const joinRoom = async () => {
    setBusy(true);
    try {
      const d = await apiFetch("/social/rooms/join", { method: "POST", body: { token: joinValue.trim() } });
      setModal(null); setJoinValue("");
      navigate(`/social/${d.room.id}`);
    } catch (e) {
      if (e.status === 402) return navigate("/paywall");
      toast(e.message);
    } finally { setBusy(false); }
  };

  const hideRoom = async (r) => {
    try {
      await apiFetch(`/social/rooms/${r.id}/hide`, { method: "POST" });
      setRooms((p) => p.filter((x) => x.id !== r.id));
      toast("Removed from your list.");
    } catch (e) { toast(e.message); }
  };
  const deleteRoom = async (r) => {
    if (!window.confirm(`Delete "${r.name}" for everyone? This can't be undone.`)) return;
    try {
      await apiFetch(`/social/rooms/${r.id}`, { method: "DELETE" });
      setRooms((p) => p.filter((x) => x.id !== r.id));
      toast("Group deleted.");
    } catch (e) { toast(e.message); }
  };

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-border/70 bg-background/80 px-4 py-3 backdrop-blur-xl">
        <button data-testid="social-back" onClick={() => navigate("/home")} className="flex items-center gap-1 text-sm font-semibold">
          <ChevronLeft className="h-5 w-5" /> Chat
        </button>
        <h1 className="font-display text-lg font-bold">Groups</h1>
        {!loading && rooms.length > 0 ? (
          <button data-testid="social-new-btn" onClick={() => { setName(""); setModal("new"); }} className="p-1">
            <Plus className="h-6 w-6" strokeWidth={1.5} />
          </button>
        ) : (
          <span className="w-8" aria-hidden />
        )}
      </header>

      <main className="max-scroll flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-2xl px-4 py-4">
          {loading ? (
            <div className="flex justify-center py-24"><Spinner className="h-6 w-6 text-foreground" /></div>
          ) : rooms.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <ConnectedCircles />
              <h2 className="font-display text-3xl font-bold sm:text-4xl">Chat with friends you know</h2>
              <p className="max-w-xs text-muted-foreground">Private groups you create or join with a link.</p>
              <div className="mt-6 flex w-full max-w-sm flex-col gap-3">
                <Button data-testid="social-empty-new" className="w-full py-4 text-base" onClick={() => setModal("new")}>Create a group</Button>
                <Button variant="secondary" data-testid="social-empty-join" className="w-full py-4 text-base" onClick={() => setModal("join")}>Join with a link</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              {rooms.map((r) => (
                <div key={r.id} data-testid={`room-row-${r.id}`} className="flex items-center gap-2 border-b border-border">
                  <button onClick={() => navigate(`/social/${r.id}`)} className="flex min-w-0 flex-1 items-center gap-3 py-4 text-left">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary">
                      <Users className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-semibold">{r.name}</span>
                        {r.maxx_present ? <Sparkles className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : null}
                        <span className="ml-auto shrink-0 text-xs text-muted-foreground">{r.member_count} {r.member_count === 1 ? "member" : "members"}</span>
                      </div>
                      <div className="truncate text-sm text-muted-foreground">{preview(r.last_message)}</div>
                    </div>
                  </button>
                  <RoomRowMenu room={r} onHide={() => hideRoom(r)} onDelete={() => deleteRoom(r)} />
                </div>
              ))}
              <button data-testid="social-join-link" onClick={() => setModal("join")} className="mt-4 text-center text-sm font-semibold text-muted-foreground">
                Have an invite link? Join a chat
              </button>
            </div>
          )}
        </div>
      </main>
      <BottomNav active="groups" />

      {modal ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center" onClick={() => setModal(null)}>
          <div className="w-full max-w-md rounded-t-2xl border border-border bg-background p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{modal === "new" ? "New chat" : "Join a chat"}</h2>
              <button onClick={() => setModal(null)}><X className="h-5 w-5" /></button>
            </div>
            {modal === "new" ? (
              <div className="flex flex-col gap-4">
                <Field label="Chat name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Study Squad" data-testid="new-room-name" />
                <Button data-testid="create-room-submit" onClick={createRoom} loading={busy}>Create & invite</Button>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <Field label="Invite link" value={joinValue} onChange={(e) => setJoinValue(e.target.value)} placeholder="Paste the invite link" data-testid="join-room-value" />
                <Button data-testid="join-room-submit" onClick={joinRoom} loading={busy}>Join</Button>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RoomRowMenu({ room, onHide, onDelete }) {
  const [open, setOpen] = useState(false);
  const isLeader = room.my_role === "leader";
  return (
    <div className="relative shrink-0 pr-1">
      <button data-testid={`room-menu-${room.id}`} onClick={() => setOpen((o) => !o)} className="p-2 text-muted-foreground">
        <MoreHorizontal className="h-5 w-5" />
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-30 mt-1 w-52 rounded-xl border border-border bg-popover p-1 shadow-lg">
            <button data-testid={`room-hide-${room.id}`} onClick={() => { setOpen(false); onHide(); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent">
              <EyeOff className="h-4 w-4" /> Remove from my list
            </button>
            {isLeader ? (
              <button data-testid={`room-delete-${room.id}`} onClick={() => { setOpen(false); onDelete(); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-destructive hover:bg-accent">
                <Trash2 className="h-4 w-4" /> Delete group for everyone
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
